import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../utils";
import {
  useGetDimensionTree,
  useDeleteDimensionItem,
  useReactivateDimensionItem,
  dimensionAdminKeys,
} from "../../../hooks/query/dimensions/dimensionAdmin";
import { dimensionsKeys } from "../../../hooks/query/dimensions/dimensions";
import PageHeader from "../../../components/text/PageHeader";
import DimensionTreeNode from "./DimensionTreeNode";
import EditDimensionItemModal from "./EditDimensionItemModal";
import DimensionItemUsageModal from "./DimensionItemUsageModal";
import LinkToParentModal from "./LinkToParentModal";
import MergeDimensionItemModal from "./MergeDimensionItemModal";

function DimensionManagementPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showInactive, setShowInactive] = useState(false);
  const [selectedUsageItem, setSelectedUsageItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [addChildParent, setAddChildParent] = useState(null);
  const [addChildDimensionName, setAddChildDimensionName] = useState(null);
  const [addRootDimension, setAddRootDimension] = useState(null);
  const [linkingItem, setLinkingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [mergingItem, setMergingItem] = useState(null);

  const {
    isOpen: isEditItemOpen,
    onOpen: onEditItemOpen,
    onClose: onEditItemClose,
  } = useDisclosure();
  const {
    isOpen: isUsageOpen,
    onOpen: onUsageOpen,
    onClose: onUsageClose,
  } = useDisclosure();
  const {
    isOpen: isLinkOpen,
    onOpen: onLinkOpen,
    onClose: onLinkClose,
  } = useDisclosure();
  const {
    isOpen: isMergeOpen,
    onOpen: onMergeOpen,
    onClose: onMergeClose,
  } = useDisclosure();

  const { data: treeData, isLoading } = useGetDimensionTree();

  const dimensions = useMemo(() => treeData?.dimensions || [], [treeData?.dimensions]);
  const level1Dimension = dimensions.length > 0 ? dimensions[0] : null;

  const refreshTree = () => {
    queryClient.invalidateQueries(dimensionAdminKeys.tree());
    queryClient.invalidateQueries(dimensionsKeys.all());
  };

  const { mutate: deleteDimensionItem } = useDeleteDimensionItem({
    onSuccess: (result) => {
      if (result.hardDeleted) {
        showSuccessToast(toast, "Item deleted permanently.");
      } else if (result.usage?.length > 0) {
        showSuccessToast(toast, `Item archived — used in ${result.usage.length} questionnaire(s).`);
        if (deletingItem) {
          setSelectedUsageItem(deletingItem);
          onUsageOpen();
        }
      } else {
        showSuccessToast(toast, "Item archived — it has report data.");
      }
      setDeletingItem(null);
      refreshTree();
    },
    onError: (err) => {
      setDeletingItem(null);
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutate: reactivateItem } = useReactivateDimensionItem({
    onSuccess: () => {
      showSuccessToast(toast, "Item reactivated.");
      refreshTree();
    },
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const handleCheckUsage = useCallback((item) => {
    setSelectedUsageItem(item);
    onUsageOpen();
  }, [onUsageOpen]);

  const handleEditItem = useCallback((item) => {
    setEditingItem(item);
    setAddChildParent(null);
    setAddChildDimensionName(null);
    setAddRootDimension(null);
    onEditItemOpen();
  }, [onEditItemOpen]);

  const handleDeleteItem = useCallback((item) => {
    if (window.confirm(`Delete "${item.name}"?`)) {
      setDeletingItem(item);
      deleteDimensionItem(item.id);
    }
  }, [deleteDimensionItem]);

  const handleReactivateItem = useCallback((item) => {
    reactivateItem(item.id);
  }, [reactivateItem]);

  const handleAddChild = useCallback((parentItem, childDimensionName) => {
    setEditingItem(null);
    setAddChildParent(parentItem);
    setAddChildDimensionName(childDimensionName);
    setAddRootDimension(null);
    onEditItemOpen();
  }, [onEditItemOpen]);

  const handleAddRootItem = () => {
    setEditingItem(null);
    setAddChildParent(null);
    setAddChildDimensionName(null);
    setAddRootDimension(level1Dimension);
    onEditItemOpen();
  };

  const handleAddUnlinkedItem = (dimension) => {
    setEditingItem(null);
    setAddChildParent(null);
    setAddChildDimensionName(null);
    setAddRootDimension(dimension);
    onEditItemOpen();
  };

  const handleLinkItem = useCallback((item) => {
    setLinkingItem(item);
    onLinkOpen();
  }, [onLinkOpen]);

  const handleMergeItem = useCallback((item) => {
    setMergingItem(item);
    onMergeOpen();
  }, [onMergeOpen]);

  const visibleRoots = useMemo(
    () => (treeData?.roots || []).filter((item) => showInactive || item.state !== "ARCHIVED"),
    [treeData?.roots, showInactive],
  );
  const visibleUnlinked = useMemo(
    () => (treeData?.unlinkedItems || []).filter((item) => showInactive || item.state !== "ARCHIVED"),
    [treeData?.unlinkedItems, showInactive],
  );

  const handleEditItemClose = () => {
    setEditingItem(null);
    setAddChildParent(null);
    setAddChildDimensionName(null);
    setAddRootDimension(null);
    onEditItemClose();
  };

  return (
    <Page>
      <Stack spacing={6} p={6} bg="pureWhite" marginLeft="10%" marginRight="10%">
        <Flex justify="space-between" align="flex-start">
          <PageHeader title="Dimension Management" subTitle="Manage your organization's dimensions for data segmentation." />
          <Flex align="center" gap={4} mt={2}>
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel htmlFor="show-inactive" mb="0" fontSize="sm" fontWeight="normal">
                Show inactive
              </FormLabel>
              <Switch
                id="show-inactive"
                size="sm"
                isChecked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            </FormControl>
          </Flex>
        </Flex>

        {/* Dimension hierarchy breadcrumb */}
        {dimensions.length > 0 && (
          <HStack spacing={2} flexWrap="wrap">
            <Text fontSize="sm" color="gray.500">
              Hierarchy:
            </Text>
            {dimensions.map((dim, index) => (
              <React.Fragment key={dim.id}>
                {index > 0 && (
                  <Text fontSize="sm" color="gray.400">
                    →
                  </Text>
                )}
                <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                  {dim.name}
                </Badge>
              </React.Fragment>
            ))}
          </HStack>
        )}

        {isLoading ? (
          <Box p={8} textAlign="center" color="gray.500">
            Loading dimensions...
          </Box>
        ) : !treeData || (!treeData.roots?.length && !treeData.unlinkedItems?.length && !dimensions.length) ? (
          <Box p={8} textAlign="center" color="gray.500">
            No dimensions configured for this organization.
          </Box>
        ) : (
          <Stack spacing={4}>
            {/* Add root item button */}
            {level1Dimension && (
              <Flex>
                <Button size="sm" variant="ghost" colorScheme="teal" onClick={handleAddRootItem}>
                  + Add {level1Dimension.name}
                </Button>
              </Flex>
            )}

            {visibleRoots.length > 0 && (
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" py={2}>
                {visibleRoots.map((item) => (
                    <DimensionTreeNode
                      key={item.id}
                      item={item}
                      depth={0}
                      showInactive={showInactive}
                      dimensions={dimensions}
                      isUnlinked={false}
                      parentItemId={null}
                      onCheckUsage={handleCheckUsage}
                      onEditItem={handleEditItem}
                      onDeleteItem={handleDeleteItem}
                      onReactivateItem={handleReactivateItem}
                      onAddChild={handleAddChild}
                      onLinkItem={handleLinkItem}
                      onMergeItem={handleMergeItem}
                    />
                  ))}
              </Box>
            )}

            {(treeData.unlinkedItems?.length > 0 || dimensions.length > 1) && (
              <Box border="1px solid" borderColor="orange.200" borderRadius="md" p={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="sm" fontWeight="semibold" color="orange.600">
                    Unlinked Items — not linked to any parent
                  </Text>
                  <HStack spacing={2}>
                    {dimensions.slice(1).map((dim) => (
                      <Button key={dim.id} size="xs" variant="ghost" colorScheme="orange" onClick={() => handleAddUnlinkedItem(dim)}>
                        + Add {dim.name}
                      </Button>
                    ))}
                  </HStack>
                </Flex>
                {visibleUnlinked.length > 0 ? (
                  visibleUnlinked.map((item) => (
                      <DimensionTreeNode
                        key={item.id}
                        item={item}
                        depth={0}
                        showInactive={showInactive}
                        dimensions={dimensions}
                        isUnlinked={true}
                        parentItemId={null}
                        onCheckUsage={handleCheckUsage}
                        onEditItem={handleEditItem}
                        onDeleteItem={handleDeleteItem}
                        onReactivateItem={handleReactivateItem}
                        onAddChild={handleAddChild}
                        onLinkItem={handleLinkItem}
                        onMergeItem={handleMergeItem}
                      />
                    ))
                ) : (
                  <Text fontSize="xs" color="gray.400">
                    No unlinked items.
                  </Text>
                )}
              </Box>
            )}
          </Stack>
        )}
      </Stack>

      {isEditItemOpen && (
        <EditDimensionItemModal
          isOpen={isEditItemOpen}
          onClose={handleEditItemClose}
          item={editingItem}
          parentItem={addChildParent}
          childDimensionName={addChildDimensionName}
          rootDimension={addRootDimension}
          onSuccess={refreshTree}
        />
      )}

      {isUsageOpen && selectedUsageItem && (
        <DimensionItemUsageModal
          isOpen={isUsageOpen}
          onClose={() => {
            setSelectedUsageItem(null);
            onUsageClose();
          }}
          item={selectedUsageItem}
        />
      )}

      {isLinkOpen && linkingItem && (
        <LinkToParentModal
          isOpen={isLinkOpen}
          onClose={() => {
            setLinkingItem(null);
            onLinkClose();
          }}
          item={linkingItem}
          dimensions={dimensions}
          onSuccess={refreshTree}
        />
      )}

      {isMergeOpen && mergingItem && (
        <MergeDimensionItemModal
          isOpen={isMergeOpen}
          onClose={() => {
            setMergingItem(null);
            onMergeClose();
          }}
          item={mergingItem}
          onSuccess={refreshTree}
        />
      )}
    </Page>
  );
}

export default WithAuthorization(DimensionManagementPage);
